import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spin, message } from 'antd';
import api from '../services/api';
import dayjs from 'dayjs';
import useBackRestore, { useScrollRestore } from '../hooks/useBackRestore';

const GOLD = '#B8892A';
const INK = '#1A1410';
const INK3 = '#6B5840';
const INK4 = '#A89070';
const BG = '#FAF8F5';
const BD2 = '#EAE5DC';
const RED = '#9B2335';
const BLUE = '#1A4F8C';
const GREEN = '#2A6645';

const STATUS_MAP = {
  live: { label: '竞拍中', bg: RED, color: '#fff' },
  preview: { label: '预览中', bg: BLUE, color: '#fff' },
  upcoming: { label: '即将开拍', bg: GOLD, color: '#fff' },
  settling: { label: '结算中', bg: GREEN, color: '#fff' },
  ended: { label: '已结束', bg: '#999', color: '#fff' },
};

const TYPE_MAP = {
  watch: '钟表', jewelry: '珠宝', bag: '箱包', mixed: '综合',
  art: '艺术品', other: '其他',
};

function useCountdown(endAt) {
  const [text, setText] = useState('');
  useEffect(() => {
    if (!endAt) return;
    const update = () => {
      const diff = dayjs(endAt).diff(dayjs(), 'second');
      if (diff <= 0) { setText('已结束'); return; }
      const d = Math.floor(diff / 86400);
      const h = Math.floor((diff % 86400) / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setText(`${d}天${h}小时${String(m).padStart(2, '0')}分钟${String(s).padStart(2, '0')}秒`);
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, [endAt]);
  return text;
}

function SessionCard({ session, onClick }) {
  const countdown = useCountdown(session.end_at);
  const isLive = session.status === 'live' || session.status === 'preview';
  const isUpcoming = session.status === 'upcoming';

  return (
    <div onClick={onClick} style={{
      background: '#fff', border: `1px solid ${BD2}`, borderRadius: 10,
      padding: '16px', marginBottom: 12, cursor: 'pointer',
      transition: 'all .12s', position: 'relative',
    }}>
      {/* 状态徽章已去掉：改用源站信息(场次名/明拍暗拍/日期) */}

      {/* Title */}
      <div style={{ fontSize: 15, fontWeight: 600, color: isLive ? RED : INK, marginBottom: 4, paddingRight: 70, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span>{session.name_zh || session.name || `${session.house_name} 场次`}</span>
        {session.bid_type && (
          <span style={{
            fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 3, flexShrink: 0,
            ...(session.bid_type === 'open'
              ? { background: 'rgba(42,102,69,.1)', color: GREEN, border: '1px solid rgba(42,102,69,.2)' }
              : { background: 'rgba(140,90,10,.1)', color: '#8C5A0A', border: '1px solid rgba(140,90,10,.2)' }),
          }}>
            {session.bid_type === 'open' ? '明拍' : '暗拍'}
          </span>
        )}
      </div>

      <div style={{ height: 1, background: BD2, margin: '10px 0' }} />

      {/* Countdown */}
      {(isLive || isUpcoming) && (
        <div style={{ marginBottom: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: INK3 }}>
            {isLive ? '剩余时间：' : '开拍时间：'}
          </span>
          <span style={{ fontSize: 14, fontWeight: 700, color: session.start_at ? RED : INK3 }}>
            {isLive ? countdown : (session.start_at ? dayjs(session.start_at).format('MM/DD HH:mm') : '时间待公布')}
          </span>
        </div>
      )}

      {/* Info */}
      <div style={{ fontSize: 12, color: INK3, lineHeight: 1.8 }}>
        <div>商品件数：{session.active_lot_count != null ? session.active_lot_count : (session.lot_count || 0)}</div>
        {session.session_type && (
          <div>场次类型：{TYPE_MAP[session.session_type] || session.session_type}</div>
        )}
        {session.end_at && (
          <div>结束时间：{dayjs(session.end_at).format('YYYY/MM/DD HH:mm')}</div>
        )}
      </div>
    </div>
  );
}

export default function AuctionHouseSessions() {
  const { houseId } = useParams();
  const navigate = useNavigate();
  // 返回恢复:从场次拍品页 back 回来时恢复 tab 与滚动位置
  const [restored, saveListState] = useBackRestore();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [houseName, setHouseName] = useState('');
  const [houseLogo, setHouseLogo] = useState('');

  useEffect(() => {
    api.get(`/auction-houses/${houseId}/sessions-hub`)
      .then(res => {
        const data = res.data || [];
        setSessions(data);
        if (data.length) {
          setHouseName(data[0].house_name);
          setHouseLogo(data[0].logo_url);
        }
      })
      .catch(() => message.error('加载失败'))
      .finally(() => setLoading(false));
  }, [houseId]);

  const [tab, setTab] = useState(() => restored?.tab || 'live');
  useEffect(() => { saveListState({ tab }); }, [saveListState, tab]);
  useScrollRestore(restored, saveListState, !loading && sessions.length > 0);
  // 正在竞拍 = 竞拍中(live) + 预览中(preview) + 即将开拍但已上拍品的场次；即将开始 = upcoming 且无拍品。
  const hasLots = (s) => (s.active_lot_count != null ? s.active_lot_count : (s.lot_count || 0)) > 0;
  // 正在竞拍 = live/preview/upcoming 且**有拍品**(排除 0 件空壳)。
  const liveSessions = sessions.filter(s => (s.status === 'live' || s.status === 'preview' || s.status === 'upcoming') && hasLots(s));
  const upcomingSessions = sessions.filter(s => s.status === 'upcoming' && !hasLots(s));
  const currentList = tab === 'live' ? liveSessions : upcomingSessions;

  return (
    <div style={{ background: BG, minHeight: '100vh', paddingBottom: 60 }}>
      {/* Header */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10, background: '#fff',
        borderBottom: `1px solid ${BD2}`, display: 'flex', alignItems: 'center',
        padding: '12px 16px', gap: 12,
      }}>
        <button onClick={() => {
          // 返回上一页(保留来源页的页码/筛选/滚动);外链直达无站内历史才兜底拍场总览
          if (window.history.length > 1) navigate(-1);
          else navigate('/auction-hub');
        }} style={{
          border: 'none', background: 'none', fontSize: 20, cursor: 'pointer',
          color: INK, padding: 0, lineHeight: 1,
        }}>&larr;</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {houseLogo && (
            <img src={houseLogo} alt="" style={{ width: 24, height: 24, borderRadius: 6, objectFit: 'contain' }} />
          )}
          <span style={{ fontSize: 16, fontWeight: 600, color: INK }}>{houseName || '会场'}</span>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '60px 0', textAlign: 'center' }}><Spin size="large" /></div>
      ) : (
        <div>
          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: `1px solid ${BD2}`, background: '#fff' }}>
            {[
              { key: 'live', label: '正在竞拍', count: liveSessions.length },
              { key: 'upcoming', label: '即将开拍', count: upcomingSessions.length },
            ].map(t => (
              <div key={t.key} onClick={() => setTab(t.key)} style={{
                flex: 1, textAlign: 'center', padding: '12px 0', cursor: 'pointer',
                fontSize: 14, fontWeight: 600,
                color: tab === t.key ? GOLD : INK3,
                borderBottom: tab === t.key ? `2px solid ${GOLD}` : '2px solid transparent',
                transition: 'all .15s',
              }}>
                {t.label} ({t.count})
              </div>
            ))}
          </div>

          {/* List */}
          <div style={{ padding: '16px' }}>
            {currentList.length === 0 ? (
              <div style={{ padding: '40px 0', textAlign: 'center', color: INK4, fontSize: 14 }}>
                暂无{tab === 'live' ? '竞拍中' : '即将开拍'}的场次
              </div>
            ) : (
              currentList.map(s => (
                <SessionCard key={s.id} session={s}
                  onClick={() => navigate(`/sessions/${s.id}/lots`)} />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
